import React from 'react';
import styled from 'styled-components';
import { useMatches, useSearchParams } from 'react-router-dom';

import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';

import { makeHelpers } from '../utils/format';
import { usePolyEvent, type EventIdentifier } from './usePolyEvent';

type PolyPredictionsEventProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: any;
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const PolyPredictionsEvent = (componentProps: PolyPredictionsEventProps) => {
  let props = componentProps;
  let isVisible = true;

  const dataElementContext = React.useContext(DataElementContext);
  [props, isVisible] = processComponentProps(props, dataElementContext);

  const uriMatches = useMatches();
  const [searchParams, setSearchParams] = useSearchParams();

  let eventSlugId: string | undefined;
  if (props.properties?.slugId) {
    eventSlugId = props.properties.slugId;
  }

  if (props.properties?.hasTargetId && props.properties?.targetIdFromPath && props.properties?.pathParamKey) {
    if (uriMatches && uriMatches.length) {
      const match = uriMatches[0];
      let slugIdFromPath = props.properties.pathParamKey;
      if (slugIdFromPath[0] === ':') slugIdFromPath = slugIdFromPath.substr(1);
      if (match.params && slugIdFromPath && match.params[slugIdFromPath] != null) {
        eventSlugId = match.params[slugIdFromPath];
      }
    }
  }

  const marketIdParam = searchParams.get('market');
  const outcomeParam = searchParams.get('outcome');
  const modeParam = searchParams.get('mode');

  const identifier: EventIdentifier | null = eventSlugId ? { slug: eventSlugId } : null;
  const { event, liveByTokenId, isLoading, error } = usePolyEvent(identifier);

  const helpers = React.useMemo(() => makeHelpers(liveByTokenId), [liveByTokenId]);

  const selectedMarket = React.useMemo(() => {
    const markets = event?.markets;
    if (!markets || markets.length === 0) return null;
    return markets.find((m) => String(m.id) === marketIdParam) ?? markets[0];
  }, [event, marketIdParam]);

  const selectedSide: 'yes' | 'no' = outcomeParam === 'no' ? 'no' : 'yes';
  const selectedOutcome = selectedMarket?.outcomes?.[selectedSide] ?? null;
  const selectedTokenId = selectedOutcome?.tokenId ?? null;
  const selectedMode: 'buy' | 'sell' = modeParam === 'sell' ? 'sell' : 'buy';

  const [value, setValueState] = React.useState('');

  React.useEffect(() => {
    setValueState('');
  }, [selectedMode]);

  const selectMarket = React.useCallback(
    (e: React.SyntheticEvent<HTMLElement>) => {
      e.stopPropagation();
      const marketId = e.currentTarget.dataset.marketId;
      if (!marketId) return;
      const next = new URLSearchParams(searchParams);
      next.set('market', marketId);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const selectOutcome = React.useCallback(
    (e: React.SyntheticEvent<HTMLElement>) => {
      e.stopPropagation();
      const side = e.currentTarget.dataset.side;
      if (side !== 'yes' && side !== 'no') return;
      const next = new URLSearchParams(searchParams);
      if (side === 'yes') next.delete('outcome');
      else next.set('outcome', side);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const selectMarketOutcome = React.useCallback(
    (e: React.SyntheticEvent<HTMLElement>) => {
      e.stopPropagation();
      const { marketId, side } = e.currentTarget.dataset;
      if (!marketId) return;
      if (side !== 'yes' && side !== 'no') return;
      const next = new URLSearchParams(searchParams);
      next.set('market', marketId);
      next.set('outcome', side);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const selectMode = React.useCallback(
    (e: React.SyntheticEvent<HTMLElement>) => {
      e.stopPropagation();
      const mode = e.currentTarget.dataset.mode;
      if (mode !== 'buy' && mode !== 'sell') return;
      const next = new URLSearchParams(searchParams);
      if (mode === 'buy') next.delete('mode');
      else next.set('mode', mode);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setValue = React.useCallback((e: React.SyntheticEvent<HTMLElement>) => {
    e.stopPropagation();
    const ds = e.currentTarget.dataset.value;
    const inputValue = (e.currentTarget as HTMLInputElement).value;
    setValueState(ds !== undefined ? ds : inputValue ?? '');
  }, []);

  const executeOrder = React.useCallback(
    async (e?: React.SyntheticEvent<HTMLElement>) => {
      if (e) e.stopPropagation();
      console.log('executeOrder', {
        mode: selectedMode,
        side: selectedSide,
        marketId: selectedMarket?.id,
        tokenId: selectedTokenId,
        value,
      });
    },
    [selectedMode, selectedSide, selectedMarket, selectedTokenId, value],
  );

  const contextValue = React.useMemo(
    () => ({
      eventSlugId,
      event,
      liveByTokenId,
      isLoading,
      error,
      helpers,
      selectedMarket,
      selectedSide,
      selectedOutcome,
      selectedTokenId,
      selectedMode,
      value,
      selectMarket,
      selectOutcome,
      selectMarketOutcome,
      selectMode,
      setValue,
      executeOrder,
      parentContext: dataElementContext,
    }),
    [
      eventSlugId,
      event,
      liveByTokenId,
      isLoading,
      error,
      helpers,
      selectedMarket,
      selectedSide,
      selectedOutcome,
      selectedTokenId,
      selectedMode,
      value,
      selectMarket,
      selectOutcome,
      selectMarketOutcome,
      selectMode,
      setValue,
      executeOrder,
      dataElementContext,
    ],
  );

  if (!isVisible) return null;

  console.log('PolyPredictionsEvent[contextValue]', contextValue);

  return (
    <ModuleElementDiv className={componentProps.className ?? ''} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default PolyPredictionsEvent;
